import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import * as React from 'react';
import { useComposedRefs } from '../../hooks/useComposedRefs';
import { useStateMachine } from '../../hooks/useStateMachine';
import { getElementRef } from '../../utils/getElementRef';
export var Presence = function Presence(props) {
  var present = props.present,
    children = props.children;
  var presence = usePresence(present);
  var child = typeof children === 'function' ? children({
    present: presence.isPresent
  }) : React.Children.only(children);
  var ref = useComposedRefs(presence.ref, getElementRef(child));
  var forceMount = typeof children === 'function';
  return forceMount || presence.isPresent ? /*#__PURE__*/React.cloneElement(child, {
    ref: ref
  }) : null;
};
Presence.displayName = 'Presence';
var PRESENCE_STATE_MACHINE = {
  mounted: {
    UNMOUNT: 'unmounted',
    ANIMATION_OUT: 'unmountSuspended'
  },
  unmountSuspended: {
    MOUNT: 'mounted',
    ANIMATION_END: 'unmounted'
  },
  unmounted: {
    MOUNT: 'mounted'
  }
};

/* -------------------------------------------------------------------------------------------------
 * usePresence
 * -----------------------------------------------------------------------------------------------*/

function usePresence(present) {
  var _React$useState = React.useState(),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    node = _React$useState2[0],
    setNode = _React$useState2[1];
  var stylesRef = React.useRef({});
  var prevPresentRef = React.useRef(present);
  var prevAnimationNameRef = React.useRef('none');
  var initialState = present ? 'mounted' : 'unmounted';
  var _useStateMachine = useStateMachine(initialState, PRESENCE_STATE_MACHINE),
    _useStateMachine2 = _slicedToArray(_useStateMachine, 2),
    state = _useStateMachine2[0],
    send = _useStateMachine2[1];
  React.useEffect(function () {
    var currentAnimationName = getAnimationName(stylesRef.current);
    prevAnimationNameRef.current = state === 'mounted' ? currentAnimationName : 'none';
  }, [state]);
  useIsomorphicLayoutEffect(function () {
    var styles = stylesRef.current;
    var wasPresent = prevPresentRef.current;
    var hasPresentChanged = wasPresent !== present;
    if (hasPresentChanged) {
      var prevAnimationName = prevAnimationNameRef.current;
      var currentAnimationName = getAnimationName(styles);
      if (present) {
        send('MOUNT');
      } else if (currentAnimationName === 'none' || (styles === null || styles === void 0 ? void 0 : styles.display) === 'none') {
        // Если нет анимации выхода или элемент скрыт, анимации не будут выполняться
        // поэтому мы мгновенно демонтируем
        send('UNMOUNT');
      } else {
        /**
         * Когда `present` изменяется на `false`, мы проверяем изменения в animation-name, чтобы
         * определить, началась ли анимация. Мы выбрали этот подход (чтение
         * вычисленных стилей), потому что нет события `animationrun`, а `animationstart`
         * срабатывает после истечения `animation-delay`, что было бы слишком поздно.
         */
        var isAnimating = prevAnimationName !== currentAnimationName;
        if (wasPresent && isAnimating) {
          send('ANIMATION_OUT');
        } else {
          send('UNMOUNT');
        }
      }
      prevPresentRef.current = present;
    }
  }, [present, send]);
  useIsomorphicLayoutEffect(function () {
    if (node) {
      var _node$ownerDocument$d;
      var timeoutId;
      var ownerWindow = (_node$ownerDocument$d = node.ownerDocument.defaultView) !== null && _node$ownerDocument$d !== void 0 ? _node$ownerDocument$d : window;
      /**
       * Запуск ANIMATION_OUT во время ANIMATION_IN вызовет событие `animationcancel`
       * для ANIMATION_IN после того, как мы вошли в состояние `unmountSuspended`. Поэтому,
       * мы убеждаемся, что запускаем ANIMATION_END только для текущей активной анимации.
       */
      var handleAnimationEnd = function handleAnimationEnd(event) {
        var currentAnimationName = getAnimationName(stylesRef.current);
        var isCurrentAnimation = currentAnimationName.includes(event.animationName);
        if (event.target === node && isCurrentAnimation) {
          // С React 18 concurrency это обновление применяется через кадр после
          // окончания анимации, создавая вспышку видимого контента. Установив
          // режим заполнения анимации на "forwards", мы заставляем узел сохранять
          // стили последнего ключевого кадра, устраняя вспышку.
          //
          // Ранее мы сбрасывали обновление через ReactDom.flushSync, но с
          // анимациями выхода это приводило к удалению узла из DOM до того,
          // как синтетическое событие animationEnd было отправлено, что означало,
          // что пользовательские обработчики событий не вызывались.
          // https://github.com/radix-ui/primitives/pull/1849
          send('ANIMATION_END');
          if (!prevPresentRef.current) {
            var currentFillMode = node.style.animationFillMode;
            node.style.animationFillMode = 'forwards';
            // Сброс стиля после того, как узел успел демонтироваться (для случаев,
            // когда компонент решает не демонтироваться). Делать это раньше, чем `setTimeout`
            // (например, с `requestAnimationFrame`) все еще вызывает вспышку.
            timeoutId = ownerWindow.setTimeout(function () {
              if (node.style.animationFillMode === 'forwards') {
                node.style.animationFillMode = currentFillMode;
              }
            });
          }
        }
      };
      var handleAnimationStart = function handleAnimationStart(event) {
        if (event.target === node) {
          // если произошла анимация, сохраняем ее имя как предыдущее.
          prevAnimationNameRef.current = getAnimationName(stylesRef.current);
        }
      };
      node.addEventListener('animationstart', handleAnimationStart);
      node.addEventListener('animationcancel', handleAnimationEnd);
      node.addEventListener('animationend', handleAnimationEnd);
      return function () {
        ownerWindow.clearTimeout(timeoutId);
        node.removeEventListener('animationstart', handleAnimationStart);
        node.removeEventListener('animationcancel', handleAnimationEnd);
        node.removeEventListener('animationend', handleAnimationEnd);
      };
    } else {
      // Переход в состояние unmounted, если узел удален преждевременно.
      // Мы избегаем делать это во время очистки, так как узел может измениться, но все еще существовать.
      send('ANIMATION_END');
      return;
    }
  }, [node, send]);
  return {
    isPresent: ['mounted', 'unmountSuspended'].includes(state),
    ref: React.useCallback(function (node) {
      if (node) {
        stylesRef.current = getComputedStyle(node);
      }
      setNode(node);
    }, [])
  };
}
function getAnimationName(styles) {
  var animationName = styles === null || styles === void 0 ? void 0 : styles.animationName;
  return animationName ? animationName.replace('\\@cian', '@cian') : 'none';
}